/**
 * Hangar Detection Utility
 *
 * Determines which building and/or unit (if any) contains an aircraft based on position.
 * Used when saving aircraft position to automatically assign the area_id and unit_id.
 *
 * Detection priority:
 * 1. Check if aircraft is inside any unit within a building
 * 2. If not in a unit, check if inside any building/area
 * 3. If outside all buildings, position is on the ramp
 */

import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'
import type { PositionType } from '@/types/allocations'
import { isOutdoorAreaType } from '@/types/areas'
import type { AreaType } from '@/types/areas'
import { calculateUnitCenterPosition } from './unit-position'

/** Result of hangar/unit detection */
export interface HangarDetectionResult {
  areaId: string | null
  unitId: string | null
  positionType: PositionType
}

/**
 * Check if a point is inside a rotated rectangle
 *
 * @param pointX - X coordinate of the point (feet)
 * @param pointZ - Z coordinate of the point (feet)
 * @param rectX - X coordinate of the rectangle center (feet)
 * @param rectZ - Z coordinate of the rectangle center (feet)
 * @param width - Rectangle width (feet)
 * @param depth - Rectangle depth (feet)
 * @param rotationDeg - Rectangle rotation in degrees
 * @returns True if point is inside the rectangle bounds
 */
function isPointInRotatedRectangle(
  pointX: number,
  pointZ: number,
  rectX: number,
  rectZ: number,
  width: number,
  depth: number,
  rotationDeg: number
): boolean {
  // Calculate relative position from rectangle center
  const relativeX = pointX - rectX
  const relativeZ = pointZ - rectZ

  // If rectangle has rotation, transform to local space (inverse rotation)
  if (rotationDeg !== 0) {
    const rotationRad = (-rotationDeg * Math.PI) / 180
    const cos = Math.cos(rotationRad)
    const sin = Math.sin(rotationRad)

    // Rotate the relative position
    const localX = relativeX * cos - relativeZ * sin
    const localZ = relativeX * sin + relativeZ * cos

    // Check if in local bounds
    const halfWidth = width / 2
    const halfDepth = depth / 2
    return (
      localX >= -halfWidth &&
      localX <= halfWidth &&
      localZ >= -halfDepth &&
      localZ <= halfDepth
    )
  }

  // No rotation - axis-aligned check
  const halfWidth = width / 2
  const halfDepth = depth / 2
  return (
    relativeX >= -halfWidth &&
    relativeX <= halfWidth &&
    relativeZ >= -halfDepth &&
    relativeZ <= halfDepth
  )
}

/**
 * Determine position type based on building type
 *
 * @param buildingType - The type of the building/area
 * @returns The appropriate position type for the allocation
 */
function getPositionTypeForBuilding(buildingType?: string): PositionType {
  if (!buildingType) return 'hangar'

  const areaType = buildingType as AreaType

  // Check if it's an outdoor area type (ramp, tiedown)
  if (isOutdoorAreaType(areaType)) {
    return 'ramp'
  }

  // Default to hangar for all enclosed building types
  // Note: maintenance is now a unit designation, not a structural type
  return 'hangar'
}

/**
 * Find which building and/or unit (if any) contains the given position
 *
 * Detection order:
 * 1. First check all units within all buildings
 * 2. Then check building bounds (for buildings without units or if not in any unit)
 * 3. Finally default to ramp if outside all buildings
 *
 * @param position - Aircraft position [x, y, z] in feet from scene center
 * @param buildings - List of buildings with units to check against
 * @returns Detection result with area_id, unit_id, and position_type
 */
export function detectHangarForPosition(
  position: [number, number, number],
  buildings: Building3DConfigExtended[]
): HangarDetectionResult {
  const [aircraftX, , aircraftZ] = position

  // PHASE 1: Check units first (higher priority)
  for (const building of buildings) {
    const buildingRotation = building.rotation ?? 0

    // Check each unit in this building
    if (building.generatedUnits && building.generatedUnits.length > 0) {
      for (const unit of building.generatedUnits) {
        // Calculate unit world position using the same transform as rendering
        const tplType = building.templateType as string | undefined
        const centerZ = tplType === 'double_row' || tplType === 'nested_t_hangar'
        const unitCenter = calculateUnitCenterPosition(
          { offset_x: unit.offset_x, offset_y: unit.offset_y, interior_width: unit.interior_width },
          { position: building.position, rotation: buildingRotation, width: building.width, depth: building.depth },
          centerZ
        )
        const unitWorldX = unitCenter.x
        const unitWorldZ = unitCenter.z

        // Combined rotation (building + unit)
        const totalRotation = buildingRotation + (unit.unit_rotation || 0)

        // Check if aircraft is inside this unit
        if (
          isPointInRotatedRectangle(
            aircraftX,
            aircraftZ,
            unitWorldX,
            unitWorldZ,
            unit.interior_width,
            unit.interior_depth,
            totalRotation
          )
        ) {
          // Aircraft is inside this unit
          const buildingType = building.typeConfig?.type as string | undefined
          return {
            areaId: building.id,
            unitId: unit.id ?? null,
            positionType: getPositionTypeForBuilding(buildingType),
          }
        }
      }
    }
  }

  // PHASE 2: Check building bounds (for buildings without units or if not in any unit)
  for (const building of buildings) {
    const [buildingX, , buildingZ] = building.position
    const rotation = building.rotation ?? 0

    // Single-row buildings render from position towards -Z (not centered in Z)
    // Double-row/nested_t_hangar render centered around position
    const tpl = building.templateType as string | undefined
    const isDoubleCentered = tpl === 'double_row' || tpl === 'nested_t_hangar'
    const localCenterOffsetZ = isDoubleCentered ? 0 : -building.depth / 2

    // Rotate the local offset into world space
    const rotRad = (rotation * Math.PI) / 180
    const effectiveCenterX = buildingX + localCenterOffsetZ * Math.sin(rotRad)
    const effectiveCenterZ = buildingZ + localCenterOffsetZ * Math.cos(rotRad)

    if (
      isPointInRotatedRectangle(
        aircraftX,
        aircraftZ,
        effectiveCenterX,
        effectiveCenterZ,
        building.width,
        building.depth,
        rotation
      )
    ) {
      // Aircraft is inside this building but not in any specific unit
      const buildingType = building.typeConfig?.type as string | undefined
      return {
        areaId: building.id,
        unitId: null,
        positionType: getPositionTypeForBuilding(buildingType),
      }
    }
  }

  // PHASE 3: Aircraft is not inside any building - it's on the ramp
  return {
    areaId: null,
    unitId: null,
    positionType: 'ramp',
  }
}
